import {
  type ComponentProps, type FC, useCallback, useMemo,
} from 'react';
import { mapAreaBorder } from '~/_shared/constants/mapObjects/mapAreaBorder.constants';
import { MapAreaColors } from '~/_shared/constants/mapObjects/mapAreaColors.constants';
import type {
  LatLng, LatLngBounds,
} from '~/_shared/types/latLng';
import {
  type GroupRadiusProximity, type IndividualRadiusProximity, isGroupRadius,
} from '~/_shared/types/proximity/proximity.types';
import type { SpreadsheetRowId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { convertColorToWebGLColor } from '~/_shared/utils/colors/colors.helpers';
import { memoizeOne } from '~/_shared/utils/memoize/memoize';
import { convertUnitToKilometers } from '~/_shared/utils/unitSystem/unitSystem.helpers';
import { MapCircleComponent } from '~/map/map/mapObjects/mapCircle/mapCircle.component';
import { MapCircleContainer } from '~/map/map/mapObjects/mapCircle/mapCircle.container';
import type { MapCircleInstance } from '~/map/map/mapObjects/mapCircle/mapCircleModel';
import { MapLabelComponent } from '~/map/map/mapObjects/mapLabel/mapLabel.component';
import type { MapObjectZIndex } from '~/map/map/mapObjects/mapObject.types';
import {
  createProximityDistanceLabelWithCalloutVisuals, getCircleLabelBoundingBox,
} from '~/map/map/proximity/proximity.utils';
import { proximitySubEntities } from '~/map/zIndexes/useIndividualOrDriveTimeProximityZIndex.hook';
import {
  calculateZIndex, ZIndexedEntity,
} from '~/map/zIndexes/zIndexRanges';
import { applyIndividualDetailsToProximity } from '~/proximity/proximity.helpers';

const getHoveredProximityHighlightZIndex = memoizeOne(() => ({
  // calculateZIndex needs to be initialized before being called.
  // This is a way to make the zIndex object stable and create it after the init.
  circleArea: calculateZIndex(proximitySubEntities.length + proximitySubEntities.indexOf('entity'),
    ZIndexedEntity.ProximityCircleHighlight),
  outlineCircle: calculateZIndex(proximitySubEntities.length + proximitySubEntities.indexOf('outline'),
    ZIndexedEntity.ProximityCircleHighlight),
  labelText: calculateZIndex(proximitySubEntities.length + proximitySubEntities.indexOf('labelText'),
    ZIndexedEntity.ProximityCircleHighlight),
  labelCallout: calculateZIndex(proximitySubEntities.length + proximitySubEntities.indexOf('label'),
    ZIndexedEntity.ProximityCircleHighlight),
} satisfies Partial<MapObjectZIndex> as MapObjectZIndex));

const { borderColor, fillColor } = MapAreaColors.highlight;
const borderColorObject = convertColorToWebGLColor(borderColor, 1);
const labelColorObject = convertColorToWebGLColor('#000', 1);
const fillColorObject = convertColorToWebGLColor(fillColor, 0.4);

type HoveredProximityHighlightProps = {
  readonly proximity: GroupRadiusProximity | IndividualRadiusProximity;
  readonly coordinates: LatLng;
  readonly rowId?: SpreadsheetRowId;
};

const createHoverDistanceLabelWithCalloutVisuals = (
  proximity: IndividualRadiusProximity | GroupRadiusProximity,
  labelBounds: LatLngBounds,
): ComponentProps<typeof MapLabelComponent>['visuals'] => {
  const { label, callout } = createProximityDistanceLabelWithCalloutVisuals(proximity, labelBounds);

  return {
    label: {
      ...label,
      color: labelColorObject,
    },
    callout: {
      ...callout,
      fillColor: borderColorObject,
    },
  };
};

export const HoveredProximityHighlightContainer: FC<HoveredProximityHighlightProps> = ({
  proximity,
  coordinates,
  rowId,
}) => {
  const proximityWithIndividualDetails = isGroupRadius(proximity) ?
    applyIndividualDetailsToProximity(proximity, rowId) :
    proximity;

  const borderWidth = Math.max(proximityWithIndividualDetails.styles.borderWidth + 2, mapAreaBorder.active.minWidth);

  const highlightCircle: MapCircleInstance = useMemo(() => ({
    ...coordinates,
    scalesWithMapZoom: true,
    strokeWeight: borderWidth,
    radius: convertUnitToKilometers(
      proximityWithIndividualDetails.data.radius,
      proximityWithIndividualDetails.data.unit,
    ),
    id: `hover_${rowId?.rowId ?? proximityWithIndividualDetails.id}`,
  }),
  [borderWidth, coordinates, proximityWithIndividualDetails.data.radius, proximityWithIndividualDetails.data.unit,
    proximityWithIndividualDetails.id, rowId?.rowId]);

  const circleVisuals = useMemo(() => {
    return {
      circleArea: {
        autoScale: true,
        borderColor: borderColorObject,
        borderWidth,
        fillColor: fillColorObject,
        interactive: false,
        staticSize: false,
        units: 'metric',
      },
    } as const;
  }, [borderWidth]);

  const renderCircle: ComponentProps<typeof MapCircleContainer>['renderCircle'] = useCallback(
    (id: string, circle: MapCircleInstance) => {
      return (
        <MapCircleComponent
          key={id}
          circle={circle}
          visuals={circleVisuals}
        />
      );
    },
    [circleVisuals]);

  const renderDistanceLabel: ComponentProps<typeof MapCircleContainer>['renderCenter'] = useCallback(
    (id: string, latLng: LatLng) => {

      const labelBounds = getCircleLabelBoundingBox(latLng,
        proximityWithIndividualDetails.data.radius,
        proximityWithIndividualDetails.data.unit);
      return (
        <MapLabelComponent
          label={{
            id,
            ...latLng,
          }}
          visuals={createHoverDistanceLabelWithCalloutVisuals(proximityWithIndividualDetails, labelBounds)}
        />
      );
    },
    [proximityWithIndividualDetails]);

  return (
    <MapCircleContainer
      zIndex={getHoveredProximityHighlightZIndex()}
      circle={highlightCircle}
      renderCircle={renderCircle}
      renderCenter={renderDistanceLabel}
    />
  );
};
